"use strict"

import React, { useEffect, useReducer, useState } from "react"
import { Button, Modal } from "react-bootstrap"
import { FormattedMessage, useIntl } from "react-intl"

import { displayRef } from "../../widgets/utils"
import { CloseButton } from "../../internationalization/LocaleFormatted"
import AreYouSureModal from "../../widgets/AreYouSureModal"
import LoaderGif from "../../widgets/LoaderGif"
import ReactSelectionElement from "../../../shared_components/widgets/ReactSelectionElement"

const initialState = {
  isLoading: false,
  editResource: [],
  queryString: "",
}

let editable_fields = [] // Fields that can change
let initial_tag_state = () => {
  return {}
} // Initial resource state that applies to all
let fields_to_complete = [] // Fields that must be complete to submit row

function formReducer(state, action) {
  switch (action.type) {
    case "row": {
      return {
        ...state,
        editResource: [...state.editResource, action.value],
      }
    }
    case "field": {
      let editResource = Object.assign([], state.editResource)
      let resource_info = { ...editResource[action.index] }

      //Update resource information
      resource_info[action.field] = action.value
      const edited_fields = editable_fields
        .map((f) => action.initial_state[f] === resource_info[f])
        .filter((f) => !f)
      resource_info["edited"] = edited_fields.length > 0
      resource_info = checkIfEntryComplete(resource_info)
      editResource[action.index] = resource_info

      return {
        ...state,
        editResource: editResource,
      }
    }
    case "clear": {
      return {
        ...state,
        ...initialState,
      }
    }
    case "submit": {
      return {
        ...state,
        isLoading: true,
      }
    }
    case "invalid-form": {
      return {
        ...state,
        isLoading: false,
      }
    }
    case "filter_query": {
      return {
        ...state,
        queryString: action.value,
      }
    }
  }
}

const getInitialResourceState = (resources, dispatch, return_resources) => {
  const mapped_resources = resources.map((resource) => {
    const data = checkIfEntryComplete({
      ...resource,
      ...initial_tag_state(resource),
    })
    dispatch({ type: "row", value: data })
    return data
  })

  if (return_resources) {
    return mapped_resources
  }
}

const checkIfEntryComplete = (obj) => {
  if (!obj.active) {
    obj.complete = true
  } else {
    let complete = true
    fields_to_complete.forEach((cat) => {
      !obj[cat] || obj[cat] === "" ? (complete = false) : null
    })
    obj.complete = complete
  }
  return obj
}

export default function EditResourceModal(props) {
  const intl = useIntl()

  useEffect(() => {
    editable_fields = props.editable_fields
    initial_tag_state = props.initial_tag_state
    fields_to_complete = props.fields_to_complete
  }, [])

  const [initialResources, setInitialResources] = useState([])
  const [state, dispatch] = useReducer(formReducer, { ...initialState })
  const { isLoading, editResource, queryString } = state
  const { ModalPart } = props

  useEffect(() => {
    const resource_info = props.step_into_resources
      ? props.resources
        ? props.resources[props.step_into_resources]
        : []
      : props.resources
    const resources = getInitialResourceState(resource_info, dispatch, true)
    setInitialResources(resources)
  }, [props.resources])

  const updateFormValue = (e, field, value, index, warning) => {
    if (e) {
      e.preventDefault()
    }
    dispatch({
      type: "field",
      field: field,
      value: value,
      index: index,
      initial_state: initialResources[index],
    })
    if (warning && field !== "warning") {
      dispatch({
        type: "field",
        field: "warning",
        value: false,
        index: index,
        initial_state: initialResources[index],
      })
    }
  }

  const [data, setData] = useState(false)
  const [verifySubmitModal, setVerifySubmitModal] = useState(false)

  const validateForm = (e) => {
    displayRef("edit_resource_gif", true)
    e.preventDefault()
    dispatch({ type: "submit" })

    let validSubmission = false
    const edited_resources = editResource.filter((r) => r.edited)
    if (edited_resources.length > 0) {
      validSubmission = true
    }

    if (validSubmission && props.additionalValidationCheck) {
      validSubmission = props.additionalValidationCheck(
        validSubmission,
        edited_resources,
        editResource,
        updateFormValue,
      )
    }

    if (validSubmission) {
      setData(edited_resources)
      setVerifySubmitModal(true)
      displayRef("edit_resource_modal", false)
    } else {
      displayRef("edit_resource_gif", false)
      dispatch({ type: "invalid-form" })
    }
  }

  const generalFormSubmission = (e) => {
    if (e) {
      e.preventDefault()
    }

    setVerifySubmitModal(false)
    displayRef("edit_resource_modal", true)
    dispatch({ type: "invalid-form" })
  }

  const submitForm = (e, data) => {
    generalFormSubmission(e)
    props.submitData(data, "edit_resource_gif")
  }

  const cancelForm = (e) => {
    generalFormSubmission(e)
    displayRef("edit_resource_gif", false)
  }

  const filterQuery = (row) => {
    // Filter certain keys on the row object based on what's found in an input
    let textMatch = false,
      completeMatch = false,
      activeMatch = false
    if (
      completeFilter === "All" ||
      (row.complete && completeFilter === "Complete") ||
      (!row.complete && completeFilter === "Incomplete")
    ) {
      completeMatch = true
    }
    if (
      activeFilter === "All" ||
      (row.active && activeFilter === "Active") ||
      (!row.active && activeFilter === "Inactive")
    ) {
      activeMatch = true
    }

    if (!queryString || queryString === "") {
      textMatch = true
    } else {
      const lowerCaseSearch = queryString.toLowerCase()
      for (const field of props.fields_to_search) {
        if (row[field] && row[field].toLowerCase().indexOf(lowerCaseSearch) >= 0) {
          textMatch = true
        }
      }
    }
    return textMatch && completeMatch && activeMatch
  }

  const allOption = {
    value: "All",
    label: intl.formatMessage({ id: "general.all", defaultMessage: "All" }),
  }
  const [completeFilter, setCompleteFilter] = useState(allOption.value)
  const completeFilterCol = 3
  const [activeFilter, setActiveFilter] = useState(allOption.value)
  const activeFilterCol = editable_fields.includes("active") ? 3 : 0

  return (
    <React.Fragment>
      {verifySubmitModal && (
        <AreYouSureModal
          show={verifySubmitModal}
          onHide={(e) => cancelForm(e)}
          data={data}
          modalBody={
            <ModalPart
              data={data}
              modal_title_id={props.modal_title_id}
              resource={props.resources}
              resource_name={props.resource_name}
              table_title={props.table_title}
              table_info={props.table_info}
            />
          }
          submitForm={submitForm}
          cancelForm={cancelForm}
        />
      )}
      <Modal
        size={"xl"}
        dialogClassName={"common-modal"}
        show={props.show}
        onHide={props.onHide}
        style={{ overflowX: "scroll", overflowY: "scroll" }}
        backdrop={"static"}
        id={"edit_resource_modal"}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <div>
              <span className={"fa fa-check-square-o"} style={{ marginRight: "20px" }} />
              <FormattedMessage
                id={`${props.modal_title_id}`}
                description={`Manage ${props.resource_name} Modal Title`}
              />
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <LoaderGif id={"edit_resource_gif"} />
          {props.warning && (
            <h6 style={{ marginTop: "10px", marginBottom: "20px" }} className={"error-message"}>
              <FormattedMessage id={"general.server.error"} description={"Server Error Message"} />
              <br />
              {props.warning}
            </h6>
          )}
          <div>
            {props.table_title && props.table_title}
            {props.fields_to_search && props.fields_to_search.length > 0 && (
              <div style={{ marginBottom: "20px" }} className={"row"}>
                <div className={`col-${12 - completeFilterCol - activeFilterCol}`}>
                  <label>{intl.formatMessage({ id: "general.filter", defaultMessage: "Filter" })}</label>
                  <input
                    id={"search-filter"}
                    className={"form-control form-control-sm"}
                    type={"text"}
                    value={queryString}
                    onChange={(e) => {
                      dispatch({ type: "filter_query", value: e.target.value })
                    }}
                  />
                </div>
                {[
                  [activeFilterCol, "Active", "Inactive", "general.active", setActiveFilter, activeFilter],
                  [
                    completeFilterCol,
                    "Complete",
                    "Incomplete",
                    "general.complete",
                    setCompleteFilter,
                    completeFilter,
                  ],
                ]
                  .filter((filter) => filter[0] > 0)
                  .map((filter, idx) => {
                    const label = intl.formatMessage({ id: filter[3], defaultMessage: filter[1] })
                    const options = [
                      allOption,
                      {
                        value: filter[1],
                        label: (
                          <p style={{ marginBottom: "0" }}>
                            <span className={"fa fa-check"} />
                            &nbsp;
                            {label}
                          </p>
                        ),
                      },
                      {
                        value: filter[2],
                        label: (
                          <p style={{ marginBottom: "0" }}>
                            <span className={"fa fa-times"} />
                            &nbsp;
                            {label}
                          </p>
                        ),
                      },
                    ]
                    return (
                      <div key={`filter${idx}`} className={`col-${filter[0]}`}>
                        <ReactSelectionElement
                          name={`filter${idx}`}
                          label={label}
                          list={options}
                          value={options.find((opt) => filter[5] === opt.value)}
                          onValueChange={(_, val) => filter[4](val?.value)}
                          getOptionLabel={(obj) => obj.label}
                          required={false}
                          isMulti={false}
                          isClearable={false}
                        />
                      </div>
                    )
                  })}
              </div>
            )}
            <table className="table table-condensed tbl_lanzatable">
              <thead>
                <tr>
                  {props.table_info.map((col, idx) => (
                    <th key={`${col}_${idx}`} className={"text-center"}>
                      <h6>
                        <FormattedMessage
                          id={col["header_id"]}
                          description={`${props.resource_name} Column Header (${col["key"]})`}
                        />
                      </h6>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {editResource.map((resource, idx) => {
                  // Maintaining the correct index references by not filtering prior to mapping
                  if (filterQuery(resource)) {
                    return (
                      <React.Fragment key={`new_${resource}_${idx}`}>
                        <tr className={props.table_row_style(resource)}>
                          {props.table_info.map((col, i) => (
                            <td className={`text-center ${col["style"](resource)}`} key={`row${idx}_col${i}`}>
                              {col["content"](resource, idx, updateFormValue, editResource)}
                            </td>
                          ))}
                        </tr>
                      </React.Fragment>
                    )
                  }
                })}
              </tbody>
            </table>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div style={{ marginRight: "auto" }}>
            <CloseButton
              onClose={(e) => {
                e.preventDefault()
                props.onHide()
              }}
              disabled={isLoading}
            />
          </div>
          {!props.readOnly && (
            <div style={{ marginLeft: "auto" }}>
              <Button
                type={"reset"}
                variant={"danger"}
                disabled={isLoading}
                onClick={(e) => {
                  e.preventDefault()
                  dispatch({ type: "clear" })
                  setActiveFilter(allOption.value)
                  setCompleteFilter(allOption.value)
                  getInitialResourceState(initialResources, dispatch, false)
                }}
              >
                <FormattedMessage id={"general.clearButton"} description={"Clear Button For Form"} />
              </Button>
              &nbsp;
              <Button
                type={"submit"}
                variant={"primary"}
                disabled={isLoading}
                onClick={(e) => validateForm(e)}
              >
                <FormattedMessage id={"general.submitButton"} description={"Submit Button For Form"} />
              </Button>
            </div>
          )}
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  )
}

export const DisabledTextInput = ({ value }) => {
  return (
    <input
      className={"input-full-width text-center"}
      type={"text"}
      disabled={true}
      value={value || ""}
      onChange={(e) => e.preventDefault()}
    />
  )
}

export const ConditionalTagTextInput = (props) => {
  const { tag, idx, value, updateFormValue } = props
  return (
    <input
      className={"input-full-width"}
      type={"text"}
      value={tag[value] || ""}
      disabled={!tag.active}
      onChange={(e) => updateFormValue(e, value, e.target.value, idx, tag.warning)}
    />
  )
}
